import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/Accordion';
import { Button } from '@/components/Button';
import { ArrowLeft } from '@/components/Icons/ArrowLeft';
import { Chat } from '@/components/Icons/Chat';
import { ContactSupportModal } from '@/components/Modal/ContactSupportModal';
import { Snackbar } from '@/components/Snackbar';
import { useUserContext } from '@/auth/UserContext';
import { useScrollFadeProgress } from '@/hooks/useScrollFadeProgress';
import { Layout } from '@/layouts/Layout';
import { ButtonVariant, Size, SnackbarVariant } from '@/types/components';
import { PersonType } from '@/types/user';
import { goToPreviousPage } from '@/utils/global';
import { sanitizeRichTextHtml } from '@/utils/html';
import React, { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  isStandalone?: boolean;
}

const { Success, Error } = SnackbarVariant;
const SUPPORT_LINK_HREF = '#support';

export const Faq: React.FC<Props> = ({ isStandalone = false }) => {
  const { t } = useTranslation();
  const { user } = useUserContext();
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const { progress: scrollProgress, handleScroll } = useScrollFadeProgress();
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbarVariant, setSnackbarVariant] = useState(Success);

  const roleKey =
    user?.personType === PersonType.Student ? 'faq.student' : 'faq.parent';
  const items = [
    ...t('faq.common', { returnObjects: true }),
    ...t(roleKey, { returnObjects: true }),
  ];

  const snackbarMessage =
    snackbarVariant === Success
      ? t('contactSupport.successSnackbar')
      : t('global.errorOccurred');

  const handleShowSnackbar = (variant: SnackbarVariant) => {
    setSnackbarVariant(variant);
    setIsSnackbarOpen(true);
  };

  const handleAnswerClick = (event: MouseEvent<HTMLDivElement>) => {
    const link = (event.target as HTMLElement).closest('a');

    if (link?.getAttribute('href') !== SUPPORT_LINK_HREF) {
      return;
    }

    event.preventDefault();
    setIsSupportModalOpen(true);
  };

  const mobileHeader = (
    <div className="relative flex w-full items-center justify-between md:hidden">
      <button
        type="button"
        onClick={goToPreviousPage}
        aria-label={t('global.back')}
        className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-foreground"
      >
        <ArrowLeft />
      </button>
      <h2
        style={{ opacity: scrollProgress }}
        className="pointer-events-none absolute left-1/2 -translate-x-1/2 text-callout-16 text-foreground"
      >
        {t('global.help')}
      </h2>
      <button
        type="button"
        onClick={() => setIsSupportModalOpen(true)}
        aria-label={t('contactSupport.title')}
        className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-foreground"
      >
        <Chat />
      </button>
    </div>
  );

  const pageContent = (
    <div className="flex w-full flex-col gap-3 pb-8 md:gap-6 md:px-8 md:pt-6">
      <h1
        style={{ opacity: 1 - scrollProgress }}
        className="px-4 pt-4 text-title-24 text-foreground transition-opacity duration-75 md:hidden"
      >
        {t('global.help')}
      </h1>

      <header className="hidden items-center justify-between gap-4 md:flex">
        <div className="flex items-start gap-4">
          {isStandalone && (
            <button
              type="button"
              onClick={goToPreviousPage}
              aria-label={t('global.back')}
              className="flex h-12 w-6 shrink-0 cursor-pointer items-center justify-center text-foreground"
            >
              <ArrowLeft />
            </button>
          )}
          <div className="flex flex-col gap-1">
            <h1 className="text-title-36 text-foreground">
              {t('faq.webTitle')}
            </h1>
            <p className="text-body-18 text-muted-foreground">
              {t('faq.webSubtitle')}
            </p>
          </div>
        </div>
        <Button
          variant={ButtonVariant.Primary}
          size={Size.Medium}
          onClick={() => setIsSupportModalOpen(true)}
        >
          <Chat className="size-6" />
          {t('contactSupport.title')}
        </Button>
      </header>

      <section className="px-4 md:overflow-hidden md:rounded-2xl md:bg-card md:px-0">
        <Accordion type="multiple" className="flex w-full flex-col md:px-6">
          {items.map(({ answer, question }, index) => (
            <AccordionItem key={index} value={`item-${index}`}>
              <AccordionTrigger>{question}</AccordionTrigger>
              <AccordionContent>
                <div
                  className="rich-text"
                  onClick={handleAnswerClick}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeRichTextHtml(answer),
                  }}
                />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </div>
  );

  if (isStandalone) {
    return (
      <>
        <div className="flex h-svh flex-col bg-background pt-4 md:h-auto md:min-h-svh md:pt-0 md:-mt-4">
          <header className="flex shrink-0 items-center bg-background px-4 pt-[calc(8px+env(safe-area-inset-top))] pb-3 md:px-8 md:pt-6 md:pb-0">
            {mobileHeader}
          </header>
          <div
            onScroll={handleScroll}
            className="flex flex-1 flex-col overflow-y-auto md:overflow-visible"
          >
            {pageContent}
          </div>
        </div>
        <ContactSupportModal
          isOpen={isSupportModalOpen}
          onClose={() => setIsSupportModalOpen(false)}
          onSuccess={() => handleShowSnackbar(Success)}
          onError={() => handleShowSnackbar(Error)}
        />
        <Snackbar
          message={snackbarMessage}
          variant={snackbarVariant}
          isOpen={isSnackbarOpen}
          onDismiss={() => setIsSnackbarOpen(false)}
        />
      </>
    );
  }

  return (
    <Layout mobileHeader={mobileHeader} onScroll={handleScroll}>
      {pageContent}
      <ContactSupportModal
        isOpen={isSupportModalOpen}
        onClose={() => setIsSupportModalOpen(false)}
        onSuccess={() => handleShowSnackbar(Success)}
        onError={() => handleShowSnackbar(Error)}
      />
      <Snackbar
        message={snackbarMessage}
        variant={snackbarVariant}
        isOpen={isSnackbarOpen}
        onDismiss={() => setIsSnackbarOpen(false)}
      />
    </Layout>
  );
};
